// Public confirmation page for billing-email confirmation links sent by
// mailer. The outbound email links to /billing-emails/confirm?token=…
// (frontend route). This page calls the API and renders a friendly
// result card — never raw JSON. No auth required.
//
// The API endpoint is idempotent: a token survives after confirmation
// so repeated calls (React 18 StrictMode double-mount in dev, user
// refresh, email client prefetch) always return the same success body
// instead of flipping to a "link invalid" 404.
import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import api from "../lib/api";
import Footer from "../components/Footer";

const STATE = { LOADING: "loading", OK: "ok", BAD: "bad" };

export default function BillingEmailConfirm() {
  const [state, setState] = useState(STATE.LOADING);
  const [email, setEmail] = useState("");
  const { search } = useLocation();

  useEffect(() => {
    const token = new URLSearchParams(search).get("token") || "";
    if (!token) {
      setState(STATE.BAD);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(
          `/billing-emails/confirm?token=${encodeURIComponent(token)}`);
        if (cancelled) return;
        if (r.data?.ok) {
          setEmail(r.data.email || "");
          setState(STATE.OK);
        } else {
          setState(STATE.BAD);
        }
      } catch (_e) {
        if (cancelled) return;
        // Any 4xx (invalid, claimed meanwhile) renders the same
        // friendly expired/invalid message.
        setState(STATE.BAD);
      }
    })();
    return () => { cancelled = true; };
  }, [search]);

  return (
    <div className="min-h-screen bg-neutral-50 flex flex-col font-sans">
      <main className="flex-1 w-full max-w-xl mx-auto px-6 pt-24 pb-16"
            data-testid="billing-email-confirm-page">
        <div className="card-flat p-10 text-center">
          {state === STATE.LOADING ? (
            <>
              <Loader2 className="mx-auto animate-spin text-neutral-400" size={40} />
              <h1 className="font-display text-2xl font-semibold mt-6"
                  data-testid="confirm-state-loading">
                Confirming…
              </h1>
              <p className="text-sm text-neutral-600 mt-2">
                Checking your confirmation link.
              </p>
            </>
          ) : state === STATE.OK ? (
            <>
              <CheckCircle2 className="mx-auto text-emerald-600" size={44} />
              <h1 className="font-display text-2xl font-semibold mt-6"
                  data-testid="confirm-state-ok">
                Billing email confirmed
              </h1>
              <p className="text-sm text-neutral-600 mt-2">
                {email
                  ? <>We'll route billing notices to <span className="font-mono">{email}</span>.</>
                  : "We'll route billing notices to this address."}
              </p>
              <div className="mt-8">
                <Link to="/" className="btn-primary inline-flex items-center">
                  back to lingua franca
                </Link>
              </div>
            </>
          ) : (
            <>
              <AlertTriangle className="mx-auto text-amber-600" size={44} />
              <h1 className="font-display text-2xl font-semibold mt-6"
                  data-testid="confirm-state-bad">
                Link expired or invalid
              </h1>
              <p className="text-sm text-neutral-600 mt-2">
                This confirmation link didn't work. It may have already
                been used, or it may have expired. You can request a new
                link from your Lingua Franca settings.
              </p>
              <div className="mt-8">
                <Link to="/settings" className="btn-primary inline-flex items-center">
                  open settings
                </Link>
              </div>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
